import getOfflineMessage from "../helpers/getOfflineMessage";
import getInstallPrompt from "../helpers/getInstallPrompt";
import isProductionHost from "../helpers/isProductionHost";
import rankBadge from "../helpers/rankBadge";

// Production only. A worker registered against the Parcel dev server outlives
// it and then serves a fully cached app off a dead server, which looks like the
// dev server working and is miserable to diagnose. Same gate as the emulator.
//
// new URL(..., import.meta.url) is Parcel's supported form; a plain "/sw.js"
// string would not be bundled. Classic worker: sw.js imports nothing.
if ("serviceWorker" in navigator && isProductionHost()) {
  navigator.serviceWorker.register(new URL("../../sw.js", import.meta.url));
}

getInstallPrompt();

// The rank ladder section. Two jobs: fill the four wheels, then reveal the
// section once when it is scrolled to.
//
// The badges come from the same rankBadge() play.html uses, so the wheels here
// cannot drift from the ones the app draws or from the one printed on the
// certificate they add up to. That costs nothing this page cannot afford:
// rank.js and rankBadge.js import nothing but each other, so no Firebase
// reaches this bundle. Hand-pasted SVG in index.html would have been the same
// bytes with a silent drift attached, and nothing would ever have checked it.
//
// By NAME, read back off each <li>, not by index into RANKS. The two fail
// differently and only one of them fails visibly: indexing means inserting a
// rung in rank.js silently shifts every wheel one place down, so Ratha would
// wear Maharatha's wheel and look perfectly fine. rankBadge() validates the
// name against RANKS itself and returns "" for one it does not know, so the
// same edit leaves an empty box instead. A missing wheel is obviously broken;
// a wrong wheel is a lie. That lookup is also why RANKS is not imported here.
const ladderSteps = document.querySelector(".ladder-steps");

if (ladderSteps) {
  ladderSteps.querySelectorAll("li").forEach(step => {
    const name = step.querySelector(".ladder-name")?.textContent.trim();
    // 48 is the wide-layout size; .ladder-badge sizes the svg to its own box in
    // CSS, so the attribute only decides what a reader with no stylesheet gets.
    step.querySelector(".ladder-badge").innerHTML = rankBadge(name, 48);
  });

  // is-ready is what applies the from-state, and it is set HERE, beside the
  // observer that clears it. If this file never runs, or IntersectionObserver
  // is missing, the ladder is simply already finished. Nothing is ever left
  // hidden waiting for a script. See the motion note in styles/index.css.
  //
  // Only when the section starts BELOW THE FOLD. This runs after first paint,
  // so arming it on a ladder the reader can already see paints the finished
  // section, blanks it, and re-staggers it — which is what a browser restoring
  // a scroll position on reload does, and it reads as a glitch rather than as
  // a reveal. There is nothing to reveal to someone already looking at it.
  const belowFold =
    ladderSteps.getBoundingClientRect().top > window.innerHeight;

  if (belowFold && "IntersectionObserver" in window) {
    ladderSteps.classList.add("is-ready");

    new IntersectionObserver(
      ([entry], observer) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        observer.disconnect(); // one-shot: it must not replay on scroll back
      },
      { threshold: 0.35 }
    ).observe(ladderSteps);
  }
}

// The landing page is entirely static, so the service worker keeps it usable
// offline. Say so, rather than disabling it.
function showOfflineBanner() {
  if (document.getElementById("offlineMessage")) return;
  let body = document.querySelector("body");
  const offlineMsgDiv = getOfflineMessage(
    "You are offline. You can still open the quiz and play any questions you have downloaded."
  );
  offlineMsgDiv.id = "offlineMessage";
  body.insertBefore(offlineMsgDiv, body.firstChild);
}

window.addEventListener("offline", showOfflineBanner);

window.addEventListener("online", () => {
  document.getElementById("offlineMessage")?.remove();
});

// A page opened while already offline never fires the "offline" event.
if (!navigator.onLine) showOfflineBanner();
